/**
 * Performance Service
 * Handles clip performance calculation and session summary rendering
 * Extracted from main.js to break circular dependencies
 */

import appState from '../core/app-state.js';
import { i18nService } from './i18n-service.js';
import * as TranscriptModule from '../modules/player/transcript.js';
import type { Clip } from '../types/clip.js';

/**
 * Performance statistics result
 */
export interface PerformanceStats {
  correct: number;
  total: number;
  percentage: number;
}

/**
 * Callback function types
 */
type SwitchModeCallback = (mode: string) => void;
type ResetClipStateCallback = () => void;
type RenderClipCallback = (clip: Clip) => void;

/**
 * Callbacks interface
 */
interface PerformanceCallbacks {
  switchMode: SwitchModeCallback;
  resetClipState: ResetClipStateCallback;
  renderClip: RenderClipCallback;
}

// Dependencies from main.js - will be registered via callback
let switchMode: SwitchModeCallback | null = null;
let resetClipState: ResetClipStateCallback | null = null;
let renderClip: RenderClipCallback | null = null;

/**
 * Register dependencies from main.js
 * @param callbacks - Object containing callback functions
 */
export function registerPerformanceCallbacks(callbacks: PerformanceCallbacks): void {
  switchMode = callbacks.switchMode;
  resetClipState = callbacks.resetClipState;
  renderClip = callbacks.renderClip;
}

/**
 * Calculate clip performance based on user answers
 * @param clipId - The clip ID to calculate performance for
 * @returns Performance stats (correct, total, percentage)
 */
export function calculateClipPerformance(clipId: string): PerformanceStats {
  const clip = appState.clips.find(c => c.id === clipId);
  
  if (!clip || !clip.questions || clip.questions.length === 0) {
    return { correct: 0, total: 0, percentage: 0 };
  }
  
  // Count correct answers from our stored answers
  let correct = 0;
  const total = clip.questions.length;
  
  // Check if we have stored answers for this clip
  if (appState.clipAnswers.has(clipId)) {
    const answers = appState.clipAnswers.get(clipId);
    
    // Count correct answers (true values in the answers array)
    correct = answers?.filter(answer => answer === true).length ?? 0;
  } else {
    // Fallback to old behavior if no tracking data
    for (let i = 0; i < clip.questions.length; i++) {
      const question = clip.questions[i];
      if (question && (i < appState.currentQuestionIndex)) {
        correct++;
      }
    }
  }
  
  const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
  
  return {
    correct,
    total,
    percentage
  };
}

/**
 * Render session summary after completing all daily set clips
 * @param aggregateStats - Aggregate statistics for the session
 */
export function renderSessionSummary(aggregateStats: PerformanceStats): void {
  const questionContainer = document.getElementById('question-container');
  if (!questionContainer) return;
  
  // Create a lightweight summary panel
  const summaryPanel = document.createElement('div');
  summaryPanel.className = 'session-summary-panel';
  summaryPanel.style.cssText = 'margin-top: 1.5rem; padding: 1.5rem; background-color: #f0fdf4; border: 2px solid #10b981; border-radius: 0.5rem;';
  summaryPanel.setAttribute('role', 'region');
  summaryPanel.setAttribute('aria-label', 'Daily practice session summary');
  
  const headline = document.createElement('h3');
  headline.textContent = i18nService.getString('practiceCompleted') || 'Today\'s Practice Completed! 🎉';
  headline.style.cssText = 'font-size: 1.25rem; font-weight: 600; color: #065f46; margin-bottom: 1rem;';
  summaryPanel.appendChild(headline);
  
  if (aggregateStats.total > 0) {
    const accuracyText = document.createElement('p');
    accuracyText.textContent = `Total accuracy: ${aggregateStats.correct} / ${aggregateStats.total} correct (${aggregateStats.percentage}%)`;
    accuracyText.style.cssText = 'font-size: 1rem; color: #047857; margin-bottom: 1rem;';
    summaryPanel.appendChild(accuracyText);
  }
  
  // Add Back to Home button
  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = 'display: flex; flex-direction: column; gap: 0.75rem;';
  
  const backButton = document.createElement('button');
  backButton.textContent = 'Back to Home';
  backButton.className = 'btn-primary';
  backButton.style.cssText = 'padding: 0.75rem 1.5rem; background-color: #10b981; color: white; font-weight: 600; border-radius: 0.375rem; border: none; cursor: pointer; font-size: 1rem;';
  backButton.setAttribute('aria-label', 'Return to home');
  
  backButton.addEventListener('click', () => {
    if (resetClipState && renderClip && switchMode) {
      switchMode('home');
    }
  });
  
  buttonContainer.appendChild(backButton);
  
  // Add replay list
  if (appState.dailySet && appState.dailySet.length > 0) {
    const replaySection = document.createElement('div');
    replaySection.style.cssText = 'margin-top: 0.5rem;';
    
    const replayLabel = document.createElement('p');
    replayLabel.textContent = 'Replay a clip:';
    replayLabel.style.cssText = 'font-size: 0.875rem; font-weight: 600; color: #047857; margin-bottom: 0.5rem;';
    replaySection.appendChild(replayLabel);
    
    const replayList = document.createElement('div');
    replayList.style.cssText = 'display: flex; flex-direction: column; gap: 0.25rem;';
    
    appState.dailySet.forEach(clipId => {
      const clip = appState.clips.find(c => c.id === clipId);
      if (clip) {
        const replayLink = document.createElement('button');
        replayLink.textContent = clip.title;
        replayLink.className = 'btn--link';
        replayLink.style.cssText = 'text-align: left; padding: 0.375rem 0.5rem; background: transparent; color: #047857; border: none; cursor: pointer; font-size: 0.875rem; text-decoration: underline;';
        replayLink.setAttribute('aria-label', `Replay ${clip.title}`);
        
        replayLink.addEventListener('click', () => {
          const clipIndex = appState.clips.findIndex(c => c.id === clipId);
          if (clipIndex >= 0 && clipIndex < appState.clips.length && resetClipState && renderClip && switchMode) {
            resetClipState();
            appState.currentClipIdx = clipIndex;
            renderClip(appState.clips[clipIndex]!);
            switchMode('exercise');
          }
        });
        
        replayList.appendChild(replayLink);
      }
    });
    
    replaySection.appendChild(replayList);
    buttonContainer.appendChild(replaySection);
  }
  
  summaryPanel.appendChild(buttonContainer);
  questionContainer.appendChild(summaryPanel);
  
  summaryPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  
  TranscriptModule.announceToScreenReader(`Today's practice completed! Total accuracy: ${aggregateStats.percentage}%. You can return to the library or replay a clip.`);
}
